import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BRAND, contrastText, getThemeColors, cardStyle, sectionHeaderStyle, sendBtnStyle } from "./themes";
import { API_BASE_URL } from "./config";
import { useIsMobile, ThemeToggle } from "./App";
import ScenarioList from "./ScenarioList";

/* ------------------------------------------------------------------ */
/*  Section components                                                 */
/* ------------------------------------------------------------------ */

/* 1. Account info -------------------------------------------------- */
function AccountInfoSection({ themeColors, username, tier, expireAt }) {
  const formattedExpiry = expireAt
    ? new Date(expireAt).toLocaleDateString()
    : "—";

  return (
    <div style={cardStyle(themeColors)}>
      <div style={sectionHeaderStyle(themeColors)}>Account</div>
      <div style={{ color: themeColors.text, fontSize: 14, lineHeight: 1.7 }}>
        <div>{username}</div>
        <div>
          Plan: <strong>{tier || "free"}</strong>
          {tier && tier !== "free" && (
            <span style={{ color: themeColors.textMuted }}> · expires {formattedExpiry}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* 2. Mode selector ------------------------------------------------- */
function ModeSelectorSection({ themeColors, mode, setMode }) {
  const modes = [
    { key: "performance", label: "Performance Feedback" },
    { key: "mentoring", label: "Mentoring (Beta)" },
  ];

  return (
    <div style={cardStyle(themeColors)}>
      <div style={sectionHeaderStyle(themeColors)}>Mode</div>
      <div style={{ display: "flex", gap: 8 }}>
        {modes.map((m) => {
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              style={{
                ...sendBtnStyle,
                background: active ? BRAND.accent : themeColors.buttonInactive,
                color: contrastText(active ? BRAND.accent : themeColors.buttonInactive),
                opacity: active ? 1 : 0.75,
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* 3. Scenario browser / initializer -------------------------------- */
function ScenarioBrowserSection({ themeColors, theme, mode, navigate, remainingTurns }) {
  const [roleplayName, setRoleplayName] = useState(
    () => localStorage.getItem("roleplay_name") || ""
  );
  const [showNameError, setShowNameError] = useState(false);
  const [showLimitBanner, setShowLimitBanner] = useState(false);


  const apiBase = mode === "mentoring" ? `${API_BASE_URL}/mentoring` : API_BASE_URL;
  const appPath = mode === "mentoring" ? "/mentoring-app" : "/app";
  const storageKey = mode === "mentoring" ? "mentoring_scenario_num" : "selected_scenario_num";

  const handleSelect = (scenarioIdx) => {
    if (!roleplayName.trim()) {
      setShowNameError(true);
      return;
    }
    if (remainingTurns !== null && remainingTurns <= 0) {
      setShowLimitBanner(true);
      return;
    } 
    localStorage.setItem("roleplay_name", roleplayName.trim());
    if (scenarioIdx !== null && scenarioIdx !== undefined) {
      localStorage.setItem(storageKey, scenarioIdx);
    } else {
      localStorage.setItem(storageKey, "");
    }
    navigate(appPath);
  };

  return (
    <div style={cardStyle(themeColors)}>
      <div style={sectionHeaderStyle(themeColors)}>Scenarios</div>
      {showLimitBanner && (
        <div style={{
          background: themeColors.cardBg,
          border: `1px solid ${BRAND.accent}`,
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 14,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: themeColors.text, fontSize: 13 }}>
            You've used all your monthly messages. Upgrade your plan to continue.
          </span>
          <button
            onClick={() => setShowLimitBanner(false)}
            style={{
              background: 'none',
              border: 'none',
              color: themeColors.textMuted,
              fontSize: 16,
              cursor: 'pointer',
              paddingLeft: 12,
            }}
          >
            ✕
          </button>
        </div>
      )}
      <div style={{ marginBottom: 14 }}>
        <label style={{
          display: "block",
          marginBottom: 6,
          color: themeColors.text,
          fontWeight: 600,
          fontSize: 14,
        }}>
          Your Roleplay Name
        </label>
        <input
          type="text"
          placeholder="e.g., Michael Scott"
          value={roleplayName}
          onChange={(e) => {
            setRoleplayName(e.target.value);
            setShowNameError(false);
          }}
          style={{
            width: "100%",
            maxWidth: 340,
            boxSizing: "border-box",
            fontSize: 15,
            fontWeight: 600,
            padding: "10px 14px",
            border: `2px solid ${showNameError ? BRAND.error : themeColors.borderColor}`,
            backgroundColor: themeColors.inputBg,
            color: themeColors.text,
            borderRadius: 8,
          }}
        />
        {showNameError && (
          <div style={{ color: BRAND.error, fontSize: 12, marginTop: 4, fontWeight: 500 }}>
            Please enter a name before starting a roleplay
          </div>
        )}
      </div>

      <ScenarioList
        theme={theme}
        apiBase={apiBase}
        onSelect={handleSelect}
      />
    </div>
  );
}

/* 4. Custom scenarios management ----------------------------------- */
function CustomScenariosSection({ themeColors, navigate }) {
  const [customScenarios, setCustomScenarios] = useState([]);
  const [customBackgrounds, setCustomBackgrounds] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/custom-scenarios`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        setCustomScenarios(data.scenarios || []);
        setCustomBackgrounds(data.backgrounds || {});
      } catch (err) {
        console.error("Error fetching custom scenarios:", err);
      }
    })();
  }, []);

  return (
    <div style={cardStyle(themeColors)}>
      <div style={sectionHeaderStyle(themeColors)}>Manage Custom Scenarios</div>
      <p style={{ color: themeColors.textMuted, fontSize: 14, margin: 0, marginBottom: 12 }}>
        Create and edit your custom scenarios.
      </p>

      {customScenarios.length > 0 && (
        <div style={{ marginBottom: 12, maxHeight: 200, overflowY: "auto" }}>
          {customScenarios.map((s, i) => (
            <div
              key={i}
              onClick={() => navigate("/custom-scenario", {
                state: {
                  index: i,
                  scenario: s,
                  background: customBackgrounds[s.Persona] || {}
                }
              })}
              style={{
                padding: "8px 12px",
                marginBottom: 6,
                borderRadius: 6,
                border: `1px solid ${themeColors.borderColor}`,
                background: themeColors.inputBg,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = BRAND.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = themeColors.borderColor;
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: themeColors.text }}>
                {s.Persona}
              </div>
              <div style={{ fontSize: 12, color: themeColors.textMuted }}>
                {s.scenario_name}
              </div>
            </div>
          ))}
        </div>
      )}
    <button
      onClick={() => navigate("/custom-scenario")}
      style={{
        ...sendBtnStyle,
        background: BRAND.spotlight,
        color: contrastText(BRAND.spotlight),
      }}
    >
    Create New Scenario
    </button>
  </div>
  );
}

/* 5. Saved evaluations / transcripts ------------------------------- */
function SavedEvaluationsSection({ themeColors }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await fetch(`${API_BASE_URL}/cleanup-vacuous-sessions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: localStorage.getItem("session_id") }),
        }).catch(() => {});
        const res = await fetch(`${API_BASE_URL}/transcripts`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        const map = new Map();
        for (const row of data.transcripts || []) {
          if (!map.has(row.session_id)) {
            map.set(row.session_id, {
              session_id: row.session_id,
              scenario_name: row.scenario_name,
              persona: row.persona,
              timestamp: row.timestamp,
              lastEdited: row.end_time,
              turns: [],
            });
          }
          map.get(row.session_id).turns.push(row);
        }
        setSessions(
          [...map.values()]
            .filter((s) => s.lastEdited)
            .sort((a, b) => new Date(b.lastEdited) - new Date(a.lastEdited))
        );
      } catch (err) {
        console.error("Error fetching transcripts:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div style={cardStyle(themeColors)}>
      <div style={sectionHeaderStyle(themeColors)}>Evaluations &amp; Transcripts</div>
      {loading ? (
        <p style={{ color: themeColors.textMuted, fontSize: 14, margin: 0 }}>Loading…</p>
      ) : sessions.length === 0 ? (
        <p style={{ color: themeColors.textMuted, fontSize: 14, margin: 0 }}>
          No sessions yet. Complete a roleplay to see your history here.
        </p>
      ) : (
        <div style={{ marginBottom: 12, maxHeight: 300, overflowY: "auto" }}>
          {sessions.map((s) => (
            <div
              key={s.session_id}
              onClick={() => navigate("/transcripts", { state: { session_id: s.session_id } })}
              style={{
                padding: "8px 12px",
                marginBottom: 6,
                borderRadius: 6,
                border: `1px solid ${themeColors.borderColor}`,
                background: themeColors.inputBg,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = BRAND.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = themeColors.borderColor; }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: themeColors.text }}>
                {s.persona || s.scenario_name}
              </div>
              <div style={{ fontSize: 12, color: themeColors.textMuted }}>
                {new Date(s.timestamp).toLocaleDateString()}  {new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} · {s.turns.length} turns
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* 6. Usage monitor ------------------------------------------------- */
function UsageSection({ themeColors, turnsUsed, turnsLimit }) {
  const pct = turnsLimit > 0 ? Math.min(100, (turnsUsed / turnsLimit) * 100) : 0;

  return (
    <div style={cardStyle(themeColors)}>
      <div style={sectionHeaderStyle(themeColors)}>Usage (30 days)</div>
      <div style={{ color: themeColors.text, fontSize: 14, marginBottom: 8 }}>
        {turnsUsed} / {turnsLimit} turns used
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: themeColors.borderColor,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 4,
            background: pct > 90 ? BRAND.error : BRAND.accent,
            transition: "width 0.3s ease",
          }}
        />
      </div>
    </div>
  );
}

/* 7. Plan management / upgrade ------------------------------------- */
function PlanSection({ themeColors, currentTier, onCheckout, checkoutLoading }) {
  const plans = [
    { key: "basic", label: "Basic", price: "$15/mo" },
    { key: "premium", label: "Premium", price: "$40/mo" },
    { key: "team", label: "Team", price: "$100/mo" },
    { key: "professional", label: "Professional", price: "$400/mo" },
    { key: "enterprise", label: "Enterprise", price: "$2000/mo" },
  ];

  return (
    <div style={cardStyle(themeColors)}>
      <div style={sectionHeaderStyle(themeColors)}>Plan</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {plans.map((p) => {
          const isCurrent =
            currentTier && currentTier.toLowerCase() === p.key;
          return (
            <button
              key={p.key}
              disabled={isCurrent || checkoutLoading !== null}
              onClick={() => onCheckout(p.key)}
              style={{
                ...sendBtnStyle,
                background: isCurrent ? themeColors.buttonInactive : BRAND.primary,
                color: contrastText(isCurrent ? themeColors.buttonInactive : BRAND.primary),
                opacity: isCurrent ? 0.6 : checkoutLoading ? 0.7 : 1,
                cursor: isCurrent ? "default" : "pointer",
              }}
            >
              {isCurrent
                ? `${p.label} (current)`
                : checkoutLoading === p.key
                ? "Redirecting…"
                : `${p.label} — ${p.price}`}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* 8. Bug report ---------------------------------------------------- */
function ContactUs({ themeColors }) {
  return (
    <div style={{ color: themeColors.textMuted, fontSize: 14, margin: 0, marginBottom: 12 }}>
      <div>
        Questions or Concerns? Contact us at{" "}
        <a href="mailto:loopensupport@lausannesolutions.com"
          style={{ color: themeColors.text, textDecoration: "underline" }}
        >
          loopensupport@lausannesolutions.com
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main dashboard                                                     */
/* ------------------------------------------------------------------ */

export default function UserDashboard({ theme, setTheme }) {
  const themeColors = getThemeColors(theme);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  /* ---------- state ---------- */
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState(null);
  const [tier, setTier] = useState(null);
  const [expireAt, setExpireAt] = useState(null);
  const [mode, setMode] = useState("performance");
  const [turnsUsed, setTurnsUsed] = useState(0);
  const [turnsLimit, setTurnsLimit] = useState(30);
  const [remainingTurns, setRemainingTurns] = useState(null);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);

  /* ---------- fetch account info on mount ---------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          method: "GET",
          credentials: "include",
        });
        if (res.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        const data = await res.json();
        setUsername(data.username || null);
        setTier(data.tier || null);
        setExpireAt(data.member_expires_at || null);
        setTurnsUsed(data.turns_used || 0);
        setTurnsLimit(data.turns_limit || 30);
        setRemainingTurns(data.remaining_Turns ?? null);
      } catch {
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  /* ---------- checkout (carried over from MyAccount) ---------- */
  async function startCheckout(planKey) {
    try {
      setCheckoutLoadingPlan(planKey);
      const res = await fetch(
        `${API_BASE_URL}/billing/create-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ plan: planKey }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || "Failed to create checkout session");
        return;
      }
      if (data.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        alert("No checkout URL returned");
      }
    } catch {
      alert("Network error while starting checkout");
    } finally {
      setCheckoutLoadingPlan(null);
    }
  }

  /* ---------- loading state ---------- */
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: themeColors.background,
          color: themeColors.text,
          fontFamily: BRAND.font,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Loading…
      </div>
    );
  }

  /* ---------- render ---------- */
  const FREE_TURNS = 30; // mirror backend constant; replace with real data when available

  return (
    <div
      style={{
        minHeight: "100vh",
        background: themeColors.background,
        fontFamily: BRAND.font,
        padding: 24,
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 28,
        }}
      >
        <img
          src="/logo_wordmark.png"
          alt="Loopen"
          style={{
            height: 40,
            width: "auto",
            objectFit: "contain",
            filter: theme === "dark" ? "invert(1) brightness(1.2)" : "none",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <button
            onClick={async () => {
              await fetch(`${API_BASE_URL}/logout`, {
                method: "POST",
                credentials: "include",
              });
              navigate("/login", { replace: true });
            }}
            style={{
              ...sendBtnStyle, 
              background: themeColors.buttonInactive, 
              color: contrastText(themeColors.buttonInactive)
            }}
          >
            Log out
          </button>
        </div>
      </div>

      {/* Dashboard layout — max 720px centred */}
      <div style={{ maxWidth: 720, margin: "0 auto" }}>

        {/* ---- Top row: account + mode ---- */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <AccountInfoSection
            themeColors={themeColors}
            username={username}
            tier={tier}
            expireAt={expireAt}
          />
          <ModeSelectorSection
            themeColors={themeColors}
            mode={mode}
            setMode={setMode}
          />
        </div>

        {/* ---- Hero: scenario browser ---- */}
        <div style={{ marginBottom: 16 }}>
          <ScenarioBrowserSection
            themeColors={themeColors}
            theme={theme}
            mode={mode}
            navigate={navigate}
            remainingTurns={remainingTurns}
          />
        </div>

        {/* ---- Second row: custom scenarios + evaluations ---- */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <CustomScenariosSection
            themeColors={themeColors}
            navigate={navigate}
          />
          <SavedEvaluationsSection themeColors={themeColors} />
        </div>

        {/* ---- Third row: usage + plan ---- */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <UsageSection
            themeColors={themeColors}
            turnsUsed={turnsUsed}
            turnsLimit={turnsLimit}
          />
          <PlanSection
            themeColors={themeColors}
            currentTier={tier}
            onCheckout={startCheckout}
            checkoutLoading={checkoutLoadingPlan}
          />
        </div>

        {/* ---- Footer: contact us ---- */}
        <ContactUs themeColors={themeColors} />

      </div>
    </div>
  );
}
